import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "articles-detail__header" }
const _hoisted_3 = { class: "articles-detail__meta" }
const _hoisted_4 = { class: "articles-detail__author" }
const _hoisted_5 = {
  key: 0,
  class: "articles-detail__cta"
}
const _hoisted_6 = { class: "articles-detail__cta-copy" }
const _hoisted_7 = {
  class: "articles-detail__pagination",
  "aria-label": "Article pagination"
}
const _hoisted_8 = ["href"]
const _hoisted_9 = ["href"]

import { computed, onMounted, ref } from 'vue';
import api from '@/api';
import utils from '@/utils';
import { shareToLinkedIn } from '@/utils/dom-utils';
import type { ArticlesDetailProps } from '@/types/components/ArticlesDetail';


export default /*@__PURE__*/_defineComponent({
  __name: 'ArticlesDetail',
  props: {
    page: {},
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;

const articleDetail = ref<any>(null);
const relatedArticles = ref<any[]>([]);
const hasComponentFields = computed(() => Object.keys(props.fields || {}).length > 0);

const currentArticle = computed(() => {
  if (hasComponentFields.value) {
    return {
      id: props.rendering?.dataSource,
      fields: props.fields,
      url: getArticleUrl({ fields: props.fields }),
    };
  }
  return articleDetail.value;
});

const detailFields = computed(() => {
  if (hasComponentFields.value) return props.fields || {};
  return articleDetail.value?.fields || articleDetail.value || props.page?.fields || {};
});

const rootClass = computed(() => utils.overall.baseComClass(detailFields.value, 'articles-detail'));
const rootStyle = computed(() => utils.overall.baseComStyle(detailFields.value));

const authorLine = computed(() => {
  const name = detailFields.value?.authorName?.value || '';
  const title = detailFields.value?.authorTitle?.value || '';
  return [name, title].filter(Boolean).join(', ');
});

const currentIndex = computed(() => {
  if (!currentArticle.value) return -1;
  return relatedArticles.value.findIndex((item) => isSameArticle(item, currentArticle.value));
});

const previousArticle = computed(() => {
  if (relatedArticles.value.length === 0) return null;
  const index = currentIndex.value;
  if (index <= 0) return null;
  return relatedArticles.value[index - 1] || null;
});

const nextArticle = computed(() => {
  if (relatedArticles.value.length === 0) return null;
  const index = currentIndex.value;
  if (index < 0) return null;
  return relatedArticles.value[index + 1] || null;
});

const getArticleTitle = (item: any) => {
  return item?.fields?.articleTitle?.value?.replace(/<[^>]+>/g, '') || '';
};

const getShareUrl = () => {
  if (detailFields.value?.shareLink?.value?.href) return detailFields.value.shareLink.value.href;
  if (typeof window === 'undefined') return '';
  return window.location.href;
};

const handleShare = () => {
  const url = getShareUrl();
  shareToLinkedIn(url);
};

const buttonFields = (button: any) => ({
  text: button?.text,
  link: button?.link,
  icon: button?.icon,
  size: { fields: button?.size?.value },
  theme: { fields: button?.theme?.value },
  type: { fields: button?.type?.value },
});

const getCurrentArticleKey = () => {
  if (typeof window === 'undefined') return '';
  return normalizeSlug(window.location.pathname.split('/').filter(Boolean).pop() || '');
};

const normalizeApiData = (res: any, ex: any) => {
  return res || ex || {};
};

const normalizeSlug = (value?: string) => {
  return decodeURIComponent(value || '')
    .trim()
    .replace(/\/+$/, '')
    .toLowerCase();
};

const normalizePath = (value?: string) => {
  const path = (value || '').split('?')[0].split('#')[0];
  return normalizeSlug(path);
};

const getArticleUrl = (item: any) => {
  return item?.url || item?.fields?.articleButton?.value?.link?.value?.href || '';
};

const isSameArticle = (source: any, target: any) => {
  if (!source || !target) return false;
  if (source.id && target.id && source.id === target.id) return true;
  if (source.name && target.name && source.name === target.name) return true;
  const sourceUrl = normalizePath(getArticleUrl(source));
  const targetUrl = normalizePath(getArticleUrl(target));
  return !!sourceUrl && !!targetUrl && sourceUrl === targetUrl;
};

const matchCurrentArticle = (item: any, currentPath: string, currentKey: string) => {
  const itemUrl = normalizePath(getArticleUrl(item));
  const itemName = normalizeSlug(item?.name || item?.displayName);
  const path = normalizePath(currentPath);

  return (
    (!!itemUrl &&
      (itemUrl === path || path.endsWith(itemUrl) || itemUrl.endsWith(`/${currentKey}`))) ||
    (!!itemName && itemName === currentKey)
  );
};

const loadArticles = async () => {
  const [res, ex] = await api.articles.getArticles({ page: 1, size: 10000 });
  const data = normalizeApiData(res, ex);
  relatedArticles.value = data?.list || [];

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const currentKey = getCurrentArticleKey();
  if (!hasComponentFields.value) {
    articleDetail.value =
      relatedArticles.value.find((item) => matchCurrentArticle(item, currentPath, currentKey)) ||
      null;
  }
};
onMounted(async () => {
  await loadArticles();
});

return (_ctx: any,_cache: any) => {
  const _component_JssRichText = _resolveComponent("JssRichText")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_SiteButton = _resolveComponent("SiteButton")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (_openBlock(), _createElementBlock("article", {
    class: _normalizeClass(rootClass.value),
    style: _normalizeStyle(rootStyle.value),
    id: __props.rendering?.dataSource
  }, [
    _createVNode(_component_ContentGrid, {
      "desktop-default-grid": [3, 18],
      "mobile-default-grid": [1, 14]
    }, {
      default: _withCtx(() => [
        _createElementVNode("header", _hoisted_2, [
          _createVNode(_component_JssRichText, {
            class: "articles-detail__title",
            field: detailFields.value?.articleTitle,
            tag: "h1"
          }, null, 8, ["field"]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", _hoisted_4, [
              _createVNode(_component_Icon, { name: "user" }),
              _createTextVNode(" " + _toDisplayString(authorLine.value), 1)
            ]),
            _createElementVNode("button", {
              class: "articles-detail__share",
              type: "button",
              "aria-label": "Share article",
              onClick: handleShare
            }, [
              _createVNode(_component_Icon, { name: "share" })
            ])
          ])
        ]),
        _createVNode(_component_JssRichText, {
          class: "articles-detail__intro",
          field: detailFields.value?.articleSubtitle
        }, null, 8, ["field"]),
        _createVNode(_component_JssRichText, {
          class: "articles-detail__body",
          field: detailFields.value?.articleBody,
          tag: "div"
        }, null, 8, ["field"]),
        (detailFields.value?.ctaTitle?.value || detailFields.value?.ctaDescription?.value)
          ? (_openBlock(), _createElementBlock("aside", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_JssRichText, {
                  class: "articles-detail__cta-title",
                  field: detailFields.value?.ctaTitle
                }, null, 8, ["field"]),
                _createVNode(_component_JssRichText, {
                  class: "articles-detail__cta-desc",
                  field: detailFields.value?.ctaDescription
                }, null, 8, ["field"])
              ]),
              (detailFields.value?.ctaButton?.value)
                ? (_openBlock(), _createBlock(_component_SiteButton, {
                    key: 0,
                    class: "articles-detail__cta-button",
                    fields: buttonFields(detailFields.value?.ctaButton?.value)
                  }, null, 8, ["fields"]))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("nav", _hoisted_7, [
          (previousArticle.value)
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                class: "articles-detail__pagination-link articles-detail__pagination-link--previous",
                href: previousArticle.value.url || '#'
              }, [
                _cache[0] || (_cache[0] = _createElementVNode("span", { class: "articles-detail__pagination-arrow articles-detail__pagination-arrow--previous" }, null, -1)),
                _createElementVNode("span", null, _toDisplayString(getArticleTitle(previousArticle.value)), 1)
              ], 8, _hoisted_8))
            : _createCommentVNode("", true),
          (nextArticle.value)
            ? (_openBlock(), _createElementBlock("a", {
                key: 1,
                class: "articles-detail__pagination-link articles-detail__pagination-link--next",
                href: nextArticle.value.url || '#'
              }, [
                _createElementVNode("span", null, _toDisplayString(getArticleTitle(nextArticle.value)), 1),
                _cache[1] || (_cache[1] = _createElementVNode("span", { class: "articles-detail__pagination-arrow articles-detail__pagination-arrow--next" }, null, -1))
              ], 8, _hoisted_9))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    })
  ], 14, _hoisted_1))
}
}

})